import { normalizeStopAction, STOP_ACTIONS, getStopRecordForOrder } from "util/warehouseReturnUtils";

export function getAdditionalCustomerDisplayName(cust) {
  if (!cust) return "";
  const fromFirstOrder = cust?.Orders?.[0]?.Customer?.name;
  if (fromFirstOrder != null && fromFirstOrder !== "") return fromFirstOrder;
  if (cust?.Customer?.name != null && cust.Customer.name !== "") return cust.Customer.name;
  return cust?.name ?? "";
}

export function getVisibleAdditionalCustomers(stop) {
  const fromStop = Array.isArray(stop?.additionalCustomers) ? stop.additionalCustomers : [];
  const fromCustomer = (stop?.customer && Array.isArray(stop.customer.additionalCustomers))
    ? stop.customer.additionalCustomers
    : [];
  return [...fromStop, ...fromCustomer].filter((cust) => !cust?.isNameHidden);
}

export function collectOrdersForStop(stop, options = {}) {
  const { isNameLess = false } = options;
  const mainHidden = isNameLess && stop?.isNameHidden === true;
  const baseOrders = mainHidden ? [] : (stop?.Orders || []);

  const additionalFromStop = (Array.isArray(stop?.additionalCustomers)
    ? stop.additionalCustomers
    : []
  )
    .filter((cust) => !(isNameLess && cust?.isNameHidden))
    .flatMap((cust) => cust?.Orders || []);

  const additionalFromCustomerProp =
    (stop?.customer && Array.isArray(stop.customer.additionalCustomers)
      ? stop.customer.additionalCustomers
      : []
    )
      .filter((cust) => !(isNameLess && cust?.isNameHidden))
      .flatMap((cust) => cust?.Orders || []);

  return [...baseOrders, ...additionalFromStop, ...additionalFromCustomerProp];
}

export function isPickupOrder(order) {
  return (
    normalizeStopAction(order?.stop_action ?? order?.stopAction) === STOP_ACTIONS.PICKUP ||
    order?.order_type === "pickup"
  );
}

export function isPackageDelivered(pkg, order, options = {}) {
  if (!pkg) return false;
  if (pkg?.removed_at) return false;

  const { scanningCodesEnabled = true } = options;

  const isReturn = order?.order_type === "return" || order?.is_return;
  const isPickup = isPickupOrder(order);

  if (!scanningCodesEnabled) {
    return !!order?.delivered_at && !pkg?.is_package_lost;
  }

  if (isReturn || isPickup) {
    return !!(pkg?.verified_at || pkg?.delivered_at);
  }

  if (pkg?.delivered_at) {
    return true;
  }

  if (order?.delivered_at && !pkg?.is_package_lost && !pkg?.qr_code) {
    return true;
  }

  return false;
}

export function getOrderDeliveryNotes(order) {
  if (Array.isArray(order?.DeliveryNotes) && order.DeliveryNotes.length > 0) {
    return order.DeliveryNotes;
  }

  if (!Array.isArray(order?.deliveries) || order.deliveries.length === 0) {
    return [];
  }

  return order.deliveries.map((entry, index) => {
    const packagesRaw = entry?.Packages ?? entry?.packages;
    let packages = [];

    if (Array.isArray(packagesRaw)) {
      packages = packagesRaw;
    } else if (typeof packagesRaw === "number" && packagesRaw > 0) {
      packages = Array.from({ length: packagesRaw }, (_, i) => ({ id: i }));
    }

    return {
      id: entry?.id ?? `${order?.id ?? "order"}-dn-${index}`,
      delivery_note_number:
        entry?.delivery_note_number ??
        entry?.deliveryNote ??
        entry?.delivery_note ??
        (entry?.id != null ? String(entry.id) : "-"),
      Packages: packages,
      _packagesCount: typeof packagesRaw === "number" ? packagesRaw : packages.length,
    };
  });
}

function getOrderPackages(order) {
  return getOrderDeliveryNotes(order).flatMap((dn) =>
    Array.isArray(dn?.Packages) ? dn.Packages : []
  );
}

export function isOrderComplete(order, options = {}) {
  if (!order) {
    return false;
  }

  const { scanningCodesEnabled = true } = options;

  if (!scanningCodesEnabled) {
    return !!order.delivered_at;
  }

  const packages = getOrderPackages(order);
  const hasQrPackages = packages.length > 0 && packages.some((p) => p.qr_code);

  if (order.delivered_at && !hasQrPackages) {
    return true;
  }

  if (packages.length > 0) {
    return packages.every((pkg) => isPackageDelivered(pkg, order, options));
  }

  return !!order.delivered_at;
}

export function areAllStopOrdersDelivered(stop, options = {}) {
  const orders = collectOrdersForStop(stop);
  return orders.length > 0 && orders.every((o) => isOrderComplete(o, options));
}

export function isPathwayStopCompleted(stop, options = {}) {
  if (!stop) return false;
  if (stop.completed_at) return true;
  const route = options?.route || (options?.pathway ? options : null);
  if (route && Array.isArray(route.Stops)) {
    const customerId = stop.id != null ? stop.id : (stop.customer_id ?? stop.customer?.id ?? null);
    const supplierLocationId = stop.supplier_location_id != null ? stop.supplier_location_id : (stop.supplierLocationId ?? stop.SupplierLocation?.id ?? null);
    if (customerId !== null) {
      const customerStop = route.Stops.find(
        (s) => s.customer_id != null && Number(s.customer_id) === Number(customerId)
      );
      if (customerStop?.completed_at || customerStop?.goods_back || Number(customerStop?.total_goods_back) > 0) return true;
    } else if (supplierLocationId != null) {
      const fixStop = route.Stops.find(
        (s) => s.customer_id == null && Number(s.supplier_location_id) === Number(supplierLocationId)
      );
      if (fixStop?.completed_at || fixStop?.goods_back || Number(fixStop?.total_goods_back) > 0) return true;
    }
  }
  return areAllStopOrdersDelivered(stop, options);
}

export function areAllStopPackagesVerified(stop) {
  const orders = collectOrdersForStop(stop);
  let totalPackages = 0;
  let verifiedCount = 0;
  orders.forEach((order) => {
    getOrderDeliveryNotes(order).forEach((dn) => {
      if (dn?.removed_at) return;
      const pkgs = Array.isArray(dn?.Packages) ? dn.Packages : [];
      const countablePkgs = pkgs.filter((p) => !p?.removed_at && !p?.is_package_lost);
      totalPackages += countablePkgs.length;
      verifiedCount += countablePkgs.filter((p) => p.verified_at != null).length;
    });
  });
  return totalPackages > 0 && verifiedCount === totalPackages;
}

export function getPathwayStopCompletedAt(stop, options = {}) {
  if (!stop) return null;
  if (stop.completed_at) return stop.completed_at;
  const route = options?.route || (options?.pathway ? options : null);
  if (route && Array.isArray(route.Stops)) {
    const customerId = stop.id != null ? stop.id : (stop.customer_id ?? stop.customer?.id ?? null);
    const supplierLocationId = stop.supplier_location_id != null ? stop.supplier_location_id : (stop.supplierLocationId ?? stop.SupplierLocation?.id ?? null);
    if (customerId !== null) {
      const customerStop = route.Stops.find(
        (s) => s.customer_id != null && Number(s.customer_id) === Number(customerId)
      );
      if (customerStop?.completed_at) return customerStop.completed_at;
      if (customerStop?.time) return customerStop.time;
    } else if (supplierLocationId != null) {
      const fixStop = route.Stops.find(
        (s) => s.customer_id == null && Number(s.supplier_location_id) === Number(supplierLocationId)
      );
      if (fixStop?.completed_at) return fixStop.completed_at;
      if (fixStop?.time) return fixStop.time;
    }
  }
  const orders = collectOrdersForStop(stop);
  if (orders.length > 0 && orders.every((o) => isOrderComplete(o))) {
    const completedOrder = orders.find((o) => o?.delivered_at) || orders[0];
    if (completedOrder?.delivered_at) {
      return completedOrder.delivered_at;
    }

    for (const order of orders) {
      for (const pkg of getOrderPackages(order)) {
        if (pkg?.verified_at) {
          return pkg.verified_at;
        }
        if (pkg?.delivered_at) {
          return pkg.delivered_at;
        }
      }
    }
  }
  return null;
}

export function getStopPackageStats(stop, options = {}) {
  const { scanningCodesEnabled = true, route, pathwayIndex } = options;
  const orders = collectOrdersForStop(stop, options);
  let totalPackages = 0;
  let deliveredPackages = 0;
  let goodsBackPackages = 0;
  let lostPackages = 0;
  let hasOrderOrDNRemoval = false;
  let hasCompletedRecord = false;
  let hasGoodsBackRecord = false;

  const targetCustomerId = stop?.id != null ? stop.id : (stop?.customer_id ?? stop?.customer?.id ?? null);
  const targetSupplierId = stop?.supplier_location_id != null ? stop.supplier_location_id : (stop?.supplierLocationId ?? stop?.SupplierLocation?.id ?? null);

  let stopRecordFromRoute = null;
  if (route && Array.isArray(route.Stops)) {
    if (targetCustomerId != null) {
      stopRecordFromRoute = route.Stops.find(
        (s) => s.customer_id != null && Number(s.customer_id) === Number(targetCustomerId)
      );
    } else if (targetSupplierId != null) {
      stopRecordFromRoute = route.Stops.find(
        (s) => s.customer_id == null && Number(s.supplier_location_id) === Number(targetSupplierId)
      );
    }
  }

  if (stopRecordFromRoute?.completed_at) hasCompletedRecord = true;
  if (stopRecordFromRoute?.goods_back || Number(stopRecordFromRoute?.total_goods_back) > 0) hasGoodsBackRecord = true;
  if (stop?.completed_at) hasCompletedRecord = true;
  if (stop?.goods_back || Number(stop?.total_goods_back) > 0) hasGoodsBackRecord = true;

  orders.forEach((order) => {
    hasOrderOrDNRemoval = hasOrderOrDNRemoval || !!order?.removed_at || !!order?.skipping_reason;

    if (order?.removed_at) return;

    const deliveryNotes = getOrderDeliveryNotes(order);
    let orderTotalPackages = 0;
    let orderDeliveredPackages = 0;
    let orderGoodsBackFromPkgs = 0;
    const hasMixedOrders =
      orders.some((o) => o?.order_type === "return") &&
      orders.some((o) => o?.order_type !== "return");

    const isReturnOrPickup =
      order?.order_type === "return" ||
      order?.is_return ||
      isPickupOrder(order);
    const stopRecord = route
      ? getStopRecordForOrder(route, order, stop, pathwayIndex)
      : (stopRecordFromRoute || stop);

    if (stopRecord?.completed_at) {
      hasCompletedRecord = true;
    }
    if (stopRecord?.goods_back || Number(stopRecord?.total_goods_back) > 0) {
      hasGoodsBackRecord = true;
    }

    const totalGoodsBack = Number(stopRecord?.total_goods_back);
    const isGoodsBack = Boolean(stopRecord?.goods_back);
    const reservationAmount = Number(stopRecord?.reservation_amount);

    deliveryNotes.forEach((dn) => {
      hasOrderOrDNRemoval = hasOrderOrDNRemoval || !!dn?.removed_at;
      const pkgs = Array.isArray(dn?.Packages) ? dn.Packages : [];
      const pkgsCount = pkgs.length > 0 ? pkgs.length : (dn?._packagesCount ?? 0);
      const countablePkgs = pkgs.filter((p) => !p?.removed_at && !p?.is_package_lost);
      const countableTotal = pkgs.length > 0 ? countablePkgs.length : pkgsCount;
      orderTotalPackages += countableTotal;

      if (scanningCodesEnabled) {
        orderDeliveredPackages += countablePkgs.filter((p) => isPackageDelivered(p, order, options)).length;
      }
      orderGoodsBackFromPkgs += pkgs.filter((p) => !!p?.removed_at).length;
      lostPackages += pkgs.filter((p) => !!p?.is_package_lost).length;
    });

    totalPackages += orderTotalPackages;

    if (isReturnOrPickup) {
      if (isGoodsBack) {
        goodsBackPackages += totalGoodsBack;
      } else {
        if (scanningCodesEnabled && orderTotalPackages > 0) {
          deliveredPackages += orderDeliveredPackages;
        } else if (order?.delivered_at || isOrderComplete(order, options)) {
          deliveredPackages += orderTotalPackages;
        } else if (stopRecord?.completed_at || stop?.completed_at || stopRecordFromRoute?.completed_at) {
          deliveredPackages += orderTotalPackages;
        }
      }
    } else {
      if (isGoodsBack) {
        goodsBackPackages += totalGoodsBack - reservationAmount;
        deliveredPackages += orderTotalPackages - (totalGoodsBack + reservationAmount);
      } else {
        if (scanningCodesEnabled && orderTotalPackages > 0) {
          deliveredPackages += orderDeliveredPackages;
        } else if (order?.delivered_at || isOrderComplete(order, options)) {
          deliveredPackages += orderTotalPackages;
        } else if (stopRecord?.completed_at || stop?.completed_at || stopRecordFromRoute?.completed_at) {
          deliveredPackages += orderTotalPackages;
        }
      }
    }
  });

  return {
    totalPackages,
    deliveredPackages,
    goodsBackPackages,
    lostPackages,
    hasOrderOrDNRemoval,
    hasCompletedRecord,
    hasGoodsBackRecord,
  };
}

export function getDeliveryStatus({
  stop,
  order,
  deliveryNote,
  routeStarted,
  routeCompleted,
  scanningCodesEnabled = true,
  route,
  pathwayIndex,
}) {
  const options = { scanningCodesEnabled, route, pathwayIndex };
  if (order != null && deliveryNote != null) {
    if (stop?.skipped_at) return "skipped";
    if (order?.removed_at) return "order_removed";

    const packages = Array.isArray(deliveryNote?.Packages) ? deliveryNote.Packages : [];
    const pkgsCount = packages.length > 0 ? packages.length : (deliveryNote?._packagesCount ?? 0);
    const countablePackages = packages.filter((p) => !p?.removed_at && !p?.is_package_lost);
    const totalPackages = packages.length > 0 ? countablePackages.length : pkgsCount;
    const orderComplete = isOrderComplete(order, options);

    if (scanningCodesEnabled && !routeCompleted && stop?.completed_at == null && order?.delivered_at == null) {
      if (totalPackages > 0 && countablePackages.every((p) => p.verified_at != null && p.delivered_at == null)) {
        return "loaded";
      }
    }

    if (deliveryNote?.removed_at) {
      return routeCompleted || orderComplete ? "unsuccessful" : "in_progress";
    }

    if (totalPackages === 0) {
      return routeCompleted || orderComplete ? "delivered" : "in_progress";
    }

    const stopRecord = route
      ? getStopRecordForOrder(route, order, stop, pathwayIndex)
      : stop;

    const totalGoodsBack = Number(stopRecord?.total_goods_back);
    const isGoodsBack = Boolean(stopRecord?.goods_back);
    const reservationAmount = Number(stopRecord?.reservation_amount);
    const isReturnOrPickup =
      order?.order_type === "return" || order?.is_return || isPickupOrder(order);

    let returnedToWarehouse = 0;
    let deliveredCount = 0;

    if (isReturnOrPickup) {
      if (isGoodsBack) {
        deliveredCount = Math.min(totalPackages, totalGoodsBack);
      } else {
        if (scanningCodesEnabled) {
          deliveredCount = countablePackages.filter((p) => isPackageDelivered(p, order, options)).length;
          if (deliveredCount < totalPackages && (orderComplete || order?.delivered_at || isPathwayStopCompleted(stop, options) || stopRecord?.completed_at || stop?.completed_at)) {
            deliveredCount = totalPackages;
          }
        } else {
          if (orderComplete || order?.delivered_at || isPathwayStopCompleted(stop, options) || stopRecord?.completed_at || stop?.completed_at) {
            deliveredCount = totalPackages;
          } else {
            deliveredCount = 0;
          }
        }
      }

      if (deliveredCount >= totalPackages && (isGoodsBack || orderComplete || order?.delivered_at || isPathwayStopCompleted(stop, options) || stopRecord?.completed_at || stop?.completed_at || routeCompleted)) {
        return "delivered";
      }
      if (deliveredCount > 0 && deliveredCount < totalPackages) {
        return "partial";
      }
      if (isGoodsBack || stopRecord?.completed_at || stop?.completed_at || routeCompleted || orderComplete) {
        return "unsuccessful";
      }
      return "in_progress";
    }

    if (isGoodsBack) {
      deliveredCount = totalPackages - (totalGoodsBack + reservationAmount);
      if (totalPackages > 0 && returnedToWarehouse >= totalPackages) {
        return "unsuccessful";
      }
      if (totalPackages > 0 && returnedToWarehouse > 0 && returnedToWarehouse < totalPackages) {
        return "partial";
      }
    } else {
      if (scanningCodesEnabled) {
        deliveredCount = countablePackages.filter((p) => isPackageDelivered(p, order, options)).length;
        if (deliveredCount < totalPackages && (orderComplete || order?.delivered_at || isPathwayStopCompleted(stop, options) || stopRecord?.completed_at || stop?.completed_at)) {
          deliveredCount = totalPackages;
        }
      } else {
        if (orderComplete || order?.delivered_at || isPathwayStopCompleted(stop, options) || stopRecord?.completed_at || stop?.completed_at) {
          deliveredCount = totalPackages;
        } else {
          deliveredCount = 0;
        }
      }
    }
    if (totalPackages > 0 && returnedToWarehouse >= totalPackages) {
      return "unsuccessful";
    }
    if (deliveredCount > 0 && deliveredCount < totalPackages) {
      return "partial";
    }
    if (deliveredCount === 0 && (isGoodsBack || stopRecord?.completed_at || stop?.completed_at || routeCompleted || orderComplete)) {
      return "unsuccessful";
    }
    if (deliveredCount > 0 && deliveredCount < totalPackages) {
      return "partial";
    }
    if (deliveredCount >= totalPackages) {
      return "delivered";
    }

    return "in_progress";
  }

  if (!stop) return "ready";

  if (stop?.skipped_at) return "skipped";

  const orders = collectOrdersForStop(stop);
  const hasRemovedOrder = orders.some((o) => !!o?.removed_at);
  if (hasRemovedOrder) return "order_removed";

  const {
    totalPackages,
    deliveredPackages,
    goodsBackPackages,
    hasCompletedRecord,
    hasGoodsBackRecord,
  } = getStopPackageStats(stop, options);

  const isCompleted =
    isPathwayStopCompleted(stop, options) ||
    hasCompletedRecord ||
    hasGoodsBackRecord ||
    !!stop?.goods_back ||
    !!stop?.completed_at;

  if (totalPackages > 0 && goodsBackPackages >= totalPackages) {
    return "unsuccessful";
  }

  if (totalPackages > 0 && goodsBackPackages > 0 && goodsBackPackages < totalPackages) {
    return "partial";
  }

  if (totalPackages > 0 && deliveredPackages === 0 && (isCompleted || hasGoodsBackRecord || routeCompleted)) {
    return "unsuccessful";
  }

  if (totalPackages > 0 && deliveredPackages > 0 && deliveredPackages < totalPackages) {
    return "partial";
  }

  if (isCompleted || routeCompleted) {
    if (totalPackages === 0 || deliveredPackages >= totalPackages) {
      return "delivered";
    }
  }

  if (scanningCodesEnabled && !routeCompleted && stop?.completed_at == null && areAllStopPackagesVerified(stop) && deliveredPackages === 0) {
    return "loaded";
  }

  if (totalPackages === 0) {
    return routeCompleted ? "delivered" : "in_progress";
  }

  const allOrdersDelivered = stop?.route_type === "start_point" && areAllStopOrdersDelivered(stop, options);

  if (allOrdersDelivered || routeCompleted) {
    if (deliveredPackages >= totalPackages) return "delivered";
    if (deliveredPackages > 0) return "partial";
    if (deliveredPackages === 0) return "unsuccessful";
  }

  return "in_progress";
}

export const STOP_STATUS_PALETTE = {
  blue: "#6F9CEB",
  gray: "#ADADAD",
  orange: "orange",
  red: "#F78D94",
  yellow: "yellow",
  openHover: "#EBEBEB",
  green: "#4CAF50",
};

const TIMELINE_CLASS_BY_STATUS = {
  ready: "ball-open",
  in_progress: "ball-open",
  loaded: "ball-open",
  delivered: "ball",
  partial: "ball-partial",
  unsuccessful: "ball-removed",
  skipped: "ball-skipped",
  cancelled: "ball-removed",
  order_removed: "ball-removed",
};

const MAP_COLOR_BY_STATUS = {
  ready: "gray",
  in_progress: "gray",
  delivered: "green",
  partial: "orange",
  unsuccessful: "red",
  skipped: "yellow",
  cancelled: "red",
  order_removed: "red",
};

export function getStatusVisual(status, context) {
  if (context === "timeline") {
    return { className: TIMELINE_CLASS_BY_STATUS[status] || "ball-open" };
  }
  if (context === "map") {
    return { color: MAP_COLOR_BY_STATUS[status] || "gray" };
  }
  return {};
}

export function getStartHouseVisual(status, options = {}) {
  const { routeStarted, deliveryStartStop, deliveryStartIndex = 0, context = "timeline" } = options;

  let paletteKey = MAP_COLOR_BY_STATUS[status] || (context === "map" ? "gray" : "blue");
  let filled = context === "map";

  if (status === "delivered") {
    paletteKey = "green";
    filled = true;
  } else if (status === "partial") {
    paletteKey = "orange";
    filled = true;
  } else if (status === "unsuccessful") {
    paletteKey = "red";
    filled = true;
  } else if (status === "skipped") {
    paletteKey = "gray";
    filled = true;
  } else if (deliveryStartIndex === 0 || deliveryStartIndex == null) {
    if (routeStarted) {
      paletteKey = "green";
      filled = true;
    }
  }

  return {
    filled,
    fill: STOP_STATUS_PALETTE[paletteKey] || (context === "map" ? STOP_STATUS_PALETTE.gray : STOP_STATUS_PALETTE.blue),
  };
}
